import {
  Badge,
  Button,
  Comments,
  DataTable,
  Field,
  Loading,
  Modal,
  Note,
  Options,
  StagePath,
  type PathOutcome,
  errorMessage,
  formatDate,
  useToast,
  type BadgeKind,
} from "@corecrm/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useBug, useBugOriginals, useBugStories, useBugs, useLogBug, useVerifyBug } from "../api/queries";
import { RecordUnavailable, closeRecord } from "./bits";
import type { Bug, BugSeverity, BugStatus, Project } from "../api/types";

/* Bugs in the portal (UX-195): the project's Bugs tab, logging a bug, a bug's detail with its comments, and confirming or rejecting a fix while the team waits
   for you to verify it. Internal bugs never show here. */

export const SEVERITIES: BugSeverity[] = ["critical", "high", "medium", "low"];
export const SEVERITY_LABEL: Record<BugSeverity, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};
const SEVERITY_KIND: Record<BugSeverity, BadgeKind> = {
  critical: "bad",
  high: "high",
  medium: "info",
  low: "mute",
};
const FINISHED: BugStatus[] = ["closed", "cant_reproduce", "wont_fix", "duplicate", "is_feature"];

const NEED_INFO_NOTE = "The team needs more information on this bug — please add a comment.";

function statusKind(status: BugStatus): BadgeKind {
  if (status === "client_verification") return "high";
  if (status === "closed") return "ok";
  if (status === "need_information") return "warn";
  if (FINISHED.includes(status)) return "mute";
  return status === "new" ? "info" : "warn";
}

export function BugsView({ project }: { project: Project }) {
  const [open, setOpen] = useState(true);
  const [logging, setLogging] = useState(false);
  // The open bug is in the address (/projects/:id/bugs/:bugId), so it can be copied and shared.
  const { bugId: viewing } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const setViewing = (id: string | null) =>
    id
      ? navigate(`/projects/${project.id}/bugs/${id}`, { state: { fromList: true } })
      : closeRecord(navigate, location.state, `/projects/${project.id}?view=bugs`);
  const bugs = useBugs(project.id, open);
  const waiting = (bugs.data ?? []).filter((b) => b.awaiting_verification);
  const needInfo = (bugs.data ?? []).filter((b) => b.status === "need_information");
  return (
    <div className="stack">
      <div className="row between">
        <label className="check">
          <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} />
          <span>Open bugs only</span>
        </label>
        {project.can_log_bugs && project.takes_bugs && (
          <Button variant="primary" onClick={() => setLogging(true)}>
            Report a bug
          </Button>
        )}
      </div>
      {project.can_log_bugs && !project.takes_bugs && (
        <Note>This project is finished, so it takes no new bugs. Ask your project manager for a change.</Note>
      )}
      {waiting.length > 0 && (
        <Note kind="acc">
          {waiting.length === 1
            ? `${waiting[0]!.key} is fixed and waiting for you to check it.`
            : `${waiting.length} fixes are waiting for you to check them.`}
        </Note>
      )}
      {needInfo.length > 0 && (
        <Note kind="acc">
          {needInfo.length === 1
            ? `${needInfo[0]!.key}: ${NEED_INFO_NOTE}`
            : `The team needs more information on ${needInfo.length} bugs — please add a comment.`}
        </Note>
      )}
      {bugs.isPending ? (
        <Loading />
      ) : (
        <DataTable
          label="Bugs"
          rows={bugs.data ?? []}
          rowKey={(b) => b.id}
          empty="No bugs reported."
          columns={[
            {
              header: "Bug",
              cell: (b) => (
                <button type="button" className="task-title" onClick={() => setViewing(b.id)}>
                  <span className="mono faint">{b.key}</span> <strong>{b.title}</strong>
                </button>
              ),
            },
            {
              header: "Severity",
              cell: (b) => <Badge kind={SEVERITY_KIND[b.severity]}>{SEVERITY_LABEL[b.severity]}</Badge>,
            },
            { header: "Status", cell: (b) => <Badge kind={statusKind(b.status)}>{b.status_label}</Badge> },
            { header: "Story", cell: (b) => (b.story ? b.story.key : "—") },
            { header: "Reported", cell: (b) => formatDate(b.created_at) },
          ]}
        />
      )}
      {logging && <LogBugDialog project={project} onClose={() => setLogging(false)} />}
      {viewing && <BugDialog project={project} bugId={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** Report a bug (the Story field links it to a story). */
export function LogBugDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const toast = useToast();
  const maintenance = project.billing_model === "maintenance";
  const originals = useBugOriginals(project.id, maintenance);
  const [forProject, setForProject] = useState<string | null>(null);
  const stories = useBugStories(project.id, forProject);
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<BugSeverity>("medium");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");
  const [environment, setEnvironment] = useState("");
  const [storyId, setStoryId] = useState("");
  const log = useLogBug(project.id);
  const submit = () =>
    log.mutate(
      {
        title: title.trim(),
        severity,
        steps_to_reproduce: steps || null,
        expected_result: expected || null,
        actual_result: actual || null,
        environment: environment || null,
        story_id: storyId || null,
        ...(forProject ? { for_project_id: forProject } : {}),
      },
      {
        onSuccess: (b) => {
          toast(`${b.key} reported. The team has been told.`);
          onClose();
        },
        onError: (e) => toast(errorMessage(e), "err"),
      },
    );
  return (
    <Modal
      wide
      title="Report a bug"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={log.isPending} disabled={!title.trim()} onClick={submit}>
            Report bug
          </Button>
        </>
      }
    >
      <div className="form">
        <Field label="What's wrong" full>
          <input type="text" value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="How bad is it">
          <select value={severity} onChange={(e) => setSeverity(e.target.value as BugSeverity)}>
            <Options list={SEVERITIES.map((s) => [s, SEVERITY_LABEL[s]] as [string, string])} />
          </select>
        </Field>
        {maintenance && (originals.data ?? []).length > 0 && (
          <Field label="Which project">
            <select
              value={forProject ?? ""}
              onChange={(e) => {
                setForProject(e.target.value || null);
                setStoryId("");
              }}
            >
              <option value="">This one</option>
              <Options list={(originals.data ?? []).map((o) => [o.id, o.name] as [string, string])} />
            </select>
          </Field>
        )}
        <Field label="Story" hint="The feature it's in (optional)">
          <select value={storyId} onChange={(e) => setStoryId(e.target.value)}>
            <option value="">Not sure</option>
            <Options list={(stories.data ?? []).map((s) => [s.id, s.title] as [string, string])} />
          </select>
        </Field>
        <Field label="Steps to reproduce" full>
          <textarea rows={4} value={steps} onChange={(e) => setSteps(e.target.value)} />
        </Field>
        <Field label="What you expected">
          <textarea rows={2} value={expected} onChange={(e) => setExpected(e.target.value)} />
        </Field>
        <Field label="What happened">
          <textarea rows={2} value={actual} onChange={(e) => setActual(e.target.value)} />
        </Field>
        <Field label="Where" hint="Browser, device, page address, app version" full>
          <input type="text" value={environment} onChange={(e) => setEnvironment(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

const BUG_LABEL: Record<BugStatus, string> = {
  new: "New",
  reproducing: "Reproducing",
  root_cause: "Root cause",
  fixing: "Fixing",
  in_review: "In review",
  client_verification: "Verification",
  closed: "Closed",
  need_information: "Need Information",
  cant_reproduce: "Can't reproduce",
  wont_fix: "Won't fix",
  duplicate: "Duplicate",
  is_feature: "Is a feature",
};
const BUG_STEPS = (
  ["new", "reproducing", "root_cause", "fixing", "in_review", "client_verification"] as BugStatus[]
).map((key) => ({ key, label: BUG_LABEL[key] }));

/** The staff team's status arrows, read-only: the main line with the current status lit and
    Closed at the end. Need Information (on hold) and the side exits (can't reproduce, won't fix,
    duplicate, is a feature) show only when that is where the bug is. */
export function BugStatusPath({ bug }: { bug: Bug }) {
  const outcomes: PathOutcome[] = [{ key: "closed", label: BUG_LABEL.closed, tone: "won" }];
  if (bug.status === "need_information")
    outcomes.push({ key: bug.status, label: BUG_LABEL[bug.status], tone: "hold" });
  else if (!BUG_STEPS.some((s) => s.key === bug.status) && bug.status !== "closed")
    outcomes.push({ key: bug.status, label: BUG_LABEL[bug.status], tone: "lost" });
  return <StagePath readOnly label="Bug status" steps={BUG_STEPS} outcomes={outcomes} current={bug.status} />;
}

function Block({ title, text }: { title: string; text: string | null }) {
  if (!text) return null;
  return (
    <div>
      <strong>{title}</strong>
      <p className="task-desc">{text}</p>
    </div>
  );
}

/** One bug: what was reported, where it stands, its comments, and Confirm / Reject while the team
    waits for you to check the fix. */
export function BugDialog({
  project,
  bugId,
  onClose,
}: {
  project: Project;
  bugId: string;
  onClose: () => void;
}) {
  const bug = useBug(project.id, bugId);
  const qc = useQueryClient();
  // A bug of another project is as unavailable as a missing one.
  const b = bug.data && bug.data.project_id === project.id ? bug.data : undefined;
  if (bug.error || (bug.data && !b))
    return <RecordUnavailable what="bug" error={bug.error} onClose={onClose} />;
  return (
    <Modal
      wide
      className="record"
      title={b ? `${b.key} · ${b.title}` : "Bug"}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {!b ? (
        <Loading />
      ) : (
        <div className="stack">
          <BugStatusPath bug={b} />
          <span className="row">
            <Badge kind={statusKind(b.status)}>{b.status_label}</Badge>
            <Badge kind={SEVERITY_KIND[b.severity]}>{SEVERITY_LABEL[b.severity]}</Badge>
            {b.story && <span className="faint">Story: {b.story.title}</span>}
            {b.for_project && <span className="faint">· {b.for_project.name}</span>}
          </span>
          <p className="faint">
            Reported {formatDate(b.created_at)}
            {b.reporter ? ` by ${b.reporter.name}` : ""}
            {b.owner_name ? ` · ${b.owner_name} is on it` : ""}
          </p>
          {b.awaiting_verification && project.can_log_bugs && <Verify project={project} bug={b} />}
          {b.status === "need_information" && <Note kind="acc">{NEED_INFO_NOTE}</Note>}
          {b.status === "is_feature" && b.feature_request_key && (
            <Note>
              It&apos;s a new feature rather than a bug: now feature request {b.feature_request_key}.
            </Note>
          )}
          <Block title="Steps to reproduce" text={b.steps_to_reproduce} />
          <Block title="What you expected" text={b.expected_result} />
          <Block title="What happened" text={b.actual_result} />
          <Block title="Where" text={b.environment} />
          <Block title="Cause" text={b.root_cause} />
          {project.can_log_bugs && <AttachFile bug={b} />}
          <Comments
            api={api}
            base={`/projects/${project.id}/comments`}
            taskId={b.id}
            onUnread={() => void qc.invalidateQueries({ queryKey: ["projects", project.id, "comments"] })}
          />
        </div>
      )}
    </Modal>
  );
}

function Verify({ project, bug }: { project: Project; bug: Bug }) {
  const verify = useVerifyBug(project.id, bug.id);
  const toast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");
  const send = (confirmed: boolean) =>
    verify.mutate(
      { confirmed, ...(comment.trim() ? { comment: comment.trim() } : {}) },
      {
        onSuccess: () => toast(confirmed ? "Thanks: the bug is closed." : "Sent back to the team to fix."),
        onError: (e) => toast(errorMessage(e), "err"),
      },
    );
  return (
    <div className="note acc stack">
      <strong>The team has fixed this. Does it work for you now?</strong>
      {rejecting && (
        <Field label="What's still wrong" full>
          <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
      )}
      <div className="row">
        <Button variant="primary" busy={verify.isPending} onClick={() => send(true)}>
          Yes, it's fixed
        </Button>
        {rejecting ? (
          <Button busy={verify.isPending} disabled={!comment.trim()} onClick={() => send(false)}>
            Send back
          </Button>
        ) : (
          <Button onClick={() => setRejecting(true)}>No, still broken</Button>
        )}
      </div>
    </div>
  );
}

function AttachFile({ bug }: { bug: Bug }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const upload = async (file: File) => {
    setBusy(true);
    try {
      await api.uploadFile(`/bugs/${bug.id}/files`, file);
      toast(`${file.name} attached`);
    } catch (e) {
      toast(errorMessage(e), "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="btn sm" aria-busy={busy}>
      Attach a screenshot or file
      <input
        type="file"
        hidden
        aria-label="Attach a file"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
        }}
      />
    </label>
  );
}
